import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/sharp-solid-svg-icons';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button, { ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { useGenericSelectionPanelFunctions } from '@/features/selectionsPanel/hooks/useGenericSelectionPanelFunctions';
import { formatAcres } from '@/features/selectionsPanel/util/determineSelectionsPanelAcres';
import type { IRealEstateFeature } from '@/models/features';
import { removeFeatureByFeatureId } from '@/redux/slices/featureSlice';
import { convertMoney } from '@/utils/common';

import { Card } from '../card';
import { CardDot } from '../card/cardDot';
import { LineItem } from './lineItem';

type Props = {
  feature: IRealEstateFeature;
};

export const ListingCard: FC<Props> = ({ feature }) => {
  const { price_per_acre, total_acres, list_price, state, county } =
    feature.properties;
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);

  const { handleMouseOn, handleMouseOff, openRealEstate } =
    useGenericSelectionPanelFunctions();

  const onClose = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(removeFeatureByFeatureId(feature));
    },
    [dispatch, feature]
  );

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      openRealEstate(feature);
    },
    [feature, openRealEstate]
  );

  useEffect(() => {
    if (isHovered) {
      handleMouseOn(feature);
    } else {
      handleMouseOff(feature);
    }
  }, [feature, handleMouseOff, handleMouseOn, isHovered]);

  return (
    <Card
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex py-3">
        <CardDot feature={feature} />
        <Typography className="mr-1 leading-5" variant="h5">
          {price_per_acre ? convertMoney(Number(price_per_acre)) : '-'}
        </Typography>
        <Typography className="leading-5">/ acre</Typography>
        <div className="flex-grow" />
        <Button
          className="p-1 leading-4 text-gray-70"
          variant={ButtonVariant.GHOST}
          onClick={onClose}
        >
          <FontAwesomeIcon className="!h-4 !w-4" icon={faClose} />
        </Button>
      </div>
      <LineItem label="Total Price" value={convertMoney(Number(list_price))} />
      <LineItem label="Acres" value={formatAcres(Number(total_acres), false)} />
      <LineItem label="Location" value={`${county}, ${state}`} />
    </Card>
  );
};
